import { useEffect, useId, useRef, type PointerEvent } from "react";
import { RotateCcw } from "lucide-react";
import { Button } from "./Button";
import { Inline } from "./Inline";
import { Stack } from "./Stack";
import { Text } from "./Text";

export type SignaturePadProps = { value: string | null; onChange: (png: string | null) => void; disabled?: boolean; label?: string };

/** Optional progressive enhancement. Typed-name signing remains the accessible
 * primary path; this fixed backing surface preserves ink across viewport resize. */
export function SignaturePad({ value, onChange, disabled = false, label = "Draw your signature (optional)" }: SignaturePadProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef<number | null>(null);
  const published = useRef<string | null>(null);
  const id = useId();
  useEffect(() => {
    const surface = canvas.current, context = surface?.getContext("2d");
    if (!surface || !context || value === published.current) return;
    context.clearRect(0, 0, surface.width, surface.height);
    if (!value || !value.startsWith("data:image/png;base64,")) { published.current = value; return; }
    let cancelled = false;
    const image = new Image();
    image.onload = () => { if (!cancelled) { context.drawImage(image, 0, 0, surface.width, surface.height); published.current = value; } };
    image.src = value;
    return () => { cancelled = true; };
  }, [value]);
  const position = (event: PointerEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return { x: (event.clientX - box.left) * event.currentTarget.width / box.width, y: (event.clientY - box.top) * event.currentTarget.height / box.height };
  };
  function begin(event: PointerEvent<HTMLCanvasElement>) {
    if (disabled || drawing.current !== null || event.button !== 0) return;
    const context = event.currentTarget.getContext("2d"); if (!context) return;
    event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId); drawing.current = event.pointerId;
    const { x, y } = position(event);
    context.strokeStyle = getComputedStyle(event.currentTarget).getPropertyValue("--signature-ink").trim();
    context.lineWidth = 2.5; context.lineCap = "round"; context.lineJoin = "round";
    context.beginPath(); context.moveTo(x, y); context.lineTo(x + .1, y + .1); context.stroke();
  }
  function move(event: PointerEvent<HTMLCanvasElement>) {
    if (disabled || drawing.current !== event.pointerId) return;
    const context = event.currentTarget.getContext("2d"), point = position(event);
    context?.lineTo(point.x, point.y); context?.stroke();
  }
  function finish(event: PointerEvent<HTMLCanvasElement>) {
    if (drawing.current !== event.pointerId) return;
    drawing.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const png = event.currentTarget.toDataURL("image/png"); published.current = png; onChange(png);
  }
  return <Stack gap={2}>
    <Inline justify="between" gap={2}><Text variant="callout" as="span">{label}</Text><Button variant="secondary" fullWidth={false} disabled={disabled || !value} icon={<RotateCcw aria-hidden size={16} />} onClick={() => { canvas.current?.getContext("2d")?.clearRect(0, 0, 600, 180); published.current = null; onChange(null); }}>Clear drawing</Button></Inline>
    <canvas ref={canvas} width={600} height={180} className="v365-signature-pad" role="img" aria-label={label} aria-describedby={id} aria-disabled={disabled} onPointerDown={begin} onPointerMove={move} onPointerUp={finish} onPointerCancel={finish} />
    <Text variant="caption" tone="secondary" as="span"><span id={id}>Use your finger, pen or mouse. Your typed name is sufficient; drawing is optional.</span></Text>
  </Stack>;
}
